import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { BadgeCheck, Minus, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useStoreStatus } from "@/context/StoreStatusContext";
import DietaryMark from "@/components/site/DietaryMark";
import { useGoogleReviews } from "@/context/GoogleReviewsContext";

const FAMILY_TUB_PREORDER_SLUGS = new Set([
  "family-tub-classic-eggless",
  "family-tub-traditional-italian",
  "family-tub-biscoff",
]);
import { toast } from "sonner";
import { Stars } from "@/components/site/Stars";
import { cldImage } from "@/lib/cldImage";

const GOOGLE_REVIEW_URL = "https://g.page/r/CdeH-owIuo6vEAI/review";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [flavor, setFlavor] = useState(null);
  const [selections, setSelections] = useState([]);
  const { addItem } = useCart();
  const { storeStatus, launchPromo } = useStoreStatus();
  const { data: googleReviews, loading: googleReviewsLoading } = useGoogleReviews();
  const storeOpen = !storeStatus || storeStatus.is_open;

  useEffect(() => {
    api.get(`/products/${slug}`).then(({ data }) => {
      setProduct(data);
      setFlavor(data.flavor || (data.flavors?.[0] ?? null));
      if (data.pack_size) setSelections(Array(data.pack_size).fill(data.flavors?.[0] || ""));
    });
  }, [slug]);

  if (!product) {
    return <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-32 text-espresso-light">Loading…</div>;
  }

  const rawImg = product.image_url?.startsWith("/api/")
    ? `${process.env.REACT_APP_BACKEND_URL}${product.image_url}`
    : product.image_url;
  const imageSrc = cldImage(rawImg, { w: 1200 });

  const isPack = product.category === "packs" && product.pack_size;
  const portionLabel = product.category === "shakes" ? "Volume" : "Weight";
  const isFamilyTubPreorder = FAMILY_TUB_PREORDER_SLUGS.has(product.slug);
  const isTrackedProduct = ["cups", "tubs"].includes(product.category);
  const isSoldOut = (
    isTrackedProduct
    && typeof product.stock_qty === "number"
    && product.stock_qty <= 0
  );
  const canAddToCart = product.is_available && !isSoldOut;

  const handleAdd = () => {
    if (!storeOpen) {
      toast.error(storeStatus?.closed_message || "The store is closed right now.");
      return;
    }
    if (isPack && selections.some((s) => !s)) {
      toast.error("Please select a flavour for each item in the pack.");
      return;
    }
    addItem(product, {
      quantity: qty,
      flavor: isPack ? null : flavor,
      selections: isPack ? selections : [],
    });
    toast.success(`${product.name} added to basket`);
  };

  const googleFiveStarReviews = [...(googleReviews?.reviews || [])]
    .filter((review) => Number(review.rating) === 5)
    .sort((first, second) => Number(second.time || 0) - Number(first.time || 0))
    .slice(0, 5);

  return (
    <main data-testid="product-page" className="mx-auto max-w-[1400px] px-4 pb-32 pt-7 md:px-10 md:py-16">
      <Link
        to="/menu"
        data-testid="product-back-to-menu"
        className="label-xs text-espresso-light link-underline"
      >
        ← Back to menu
      </Link>

      <div className="mt-8 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-7">
          <div className="relative aspect-[4/3] max-h-[440px] overflow-hidden bg-ivory-100">
            {imageSrc && <img src={imageSrc} alt={product.name} className="w-full h-full object-cover" />}
            {isSoldOut && (
              <div
                data-testid="product-sold-out-overlay"
                className="absolute inset-0 flex items-center justify-center bg-espresso/55"
              >
                <span className="border border-ivory bg-espresso px-5 py-2.5 text-xs font-semibold tracking-[0.16em] text-ivory">
                  SOLD OUT
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="md:col-span-5 md:pl-6 flex flex-col">
          <div className="label-xs text-espresso-light">{product.category}</div>
          {isFamilyTubPreorder && (
            <div
              className="mt-4 border border-gold bg-gold/10 px-3 py-2 label-xs text-espresso"
              data-testid="family-tub-preorder-badge"
            >
              PRE-ORDER — 24 HOURS NOTICE REQUIRED
            </div>
          )}
          <div className="mt-3 flex items-start gap-3">
            <h1
              className={
                "font-serif text-5xl font-bold leading-[0.95] tracking-tight "
                + "text-espresso md:text-6xl"
              }
              data-testid="product-name"
            >
              {product.name}
            </h1>
            <DietaryMark
              dietaryType={product.dietary_type}
              testId={`dietary-symbol-${product.slug}`}
            />
          </div>
          {googleReviews?.rating != null && (
            <a
              href="#reviews"
              className="mt-4 inline-flex items-baseline gap-3 group"
              data-testid="product-google-rating-link"
            >
              <Stars value={googleReviews.rating} size={16} showValue />
              <span className="text-sm text-espresso-light group-hover:text-espresso">
                {googleReviews.total || 0} Google review{googleReviews.total === 1 ? "" : "s"} ·
                {" "}Read all →
              </span>
            </a>
          )}
          <div className="mt-6 font-serif text-3xl text-espresso font-bold" data-testid="product-price">₹{product.price}</div>
          <p className="mt-6 text-espresso-light leading-relaxed font-normal">
            {product.long_description || product.description}
          </p>
          <p data-testid="product-direct-order-saving-copy" className="mt-4 text-sm text-gold-hover">
            Order direct and save — our prices here are lower than on delivery apps.
          </p>
          {isSoldOut && (
            <p
              data-testid="product-sold-out-message"
              className="mt-5 border-l-2 border-rose-700 bg-rose-50 px-4 py-3 text-sm leading-relaxed text-espresso"
            >
              We&apos;re currently out of this flavor. Check back tomorrow!
            </p>
          )}
          {isFamilyTubPreorder && (
            <p
              className={
                "mt-4 border-l-2 border-gold pl-4 text-sm leading-relaxed "
                + "text-espresso-light"
              }
              data-testid="family-tub-preorder-note"
            >
              Our Family Tubs are made fresh for your order. Please place your order at least 24
              hours before your preferred delivery time.
            </p>
          )}

          {isPack && (
            <div className="mt-10">
              <div className="label-xs text-espresso-light mb-4">Choose {product.pack_size} flavours</div>
              <div className="space-y-3">
                {selections.map((sel, i) => (
                  <div key={i} className="flex items-center gap-4 border border-line px-4 py-3">
                    <span className="label-xs w-8 text-espresso-light">0{i + 1}</span>
                    <select
                      value={sel}
                      onChange={(e) => { const copy = [...selections]; copy[i] = e.target.value; setSelections(copy); }}
                      data-testid={`pack-select-${i}`}
                      className="flex-1 bg-transparent focus:outline-none font-serif text-lg text-espresso"
                    >
                      {product.flavors.map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}

          <dl
            className="mt-10 space-y-3 border-t border-line pt-6 text-sm text-espresso-light"
            data-testid="product-care-details"
          >
            <div className="flex flex-wrap gap-x-3 gap-y-1">
              <dt className="label-xs text-espresso" data-testid="product-portion-label">
                {portionLabel}
              </dt>
              <dd data-testid="product-portion-value">
                {product.weight || "To be confirmed"}
              </dd>
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-1">
              <dt className="label-xs text-espresso" data-testid="product-storage-label">
                Storage
              </dt>
              <dd data-testid="product-storage-value">
                {product.storage || "Refrigerate. Best within 24 hours."}
              </dd>
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-1">
              <dt className="label-xs text-espresso" data-testid="product-allergens-label">
                Allergens
              </dt>
              <dd data-testid="product-allergens-value">
                {product.allergens || "Contains dairy. May contain traces of nuts."}
              </dd>
            </div>
            {product.dietary_type && (
              <div
                className="mt-2 text-xs font-semibold text-espresso"
                data-testid="product-dietary-label"
              >
                {product.dietary_type === "non-veg"
                  ? "Contains Egg"
                  : "100% Eggless / Vegetarian"}
              </div>
            )}
          </dl>

          <div className="fixed inset-x-0 bottom-16 z-40 flex items-center gap-3 border-t border-line bg-ivory/95 px-4 py-3 backdrop-blur-xl md:static md:mt-10 md:gap-6 md:border-0 md:bg-transparent md:p-0 md:backdrop-blur-none">
            <div className="flex items-center gap-3 rounded-full border border-line px-3 py-2 md:gap-4 md:px-4 md:py-3">
              <button
                onClick={() => setQty(Math.max(1, qty - 1))}
                data-testid="product-qty-dec"
              >
                <Minus size={16} strokeWidth={1.5} />
              </button>
              <span className="w-8 text-center" data-testid="product-qty">{qty}</span>
              <button
                onClick={() => setQty(qty + 1)}
                data-testid="product-qty-inc"
              >
                <Plus size={16} strokeWidth={1.5} />
              </button>
            </div>
            <button
              onClick={handleAdd}
              disabled={!canAddToCart || !storeOpen}
              data-testid="product-add-btn"
              className="btn-espresso flex-1 px-4 text-center disabled:opacity-40 md:px-7"
            >
              {isSoldOut
                ? "Out of Stock"
                : !storeOpen
                  ? "Closed today"
                  : canAddToCart
                    ? <><span className="md:hidden">Add · ₹{product.price * qty}</span><span className="hidden md:inline">Add to basket</span></>
                    : "Currently unavailable"}
            </button>
          </div>

          {storeOpen && canAddToCart && launchPromo?.is_active && launchPromo?.remaining > 0 && (
            <div
              data-testid="product-cookie-peek"
              className="mt-6 border border-gold/40 bg-gold/5 px-4 py-3 rounded-sm flex items-start gap-3"
            >
              <span aria-hidden className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-gold/20 text-gold-hover text-sm">✦</span>
              <div className="min-w-0">
                <p className="font-serif italic text-espresso text-sm md:text-base leading-snug">
                  A complimentary launch cookie is on us with this order.
                </p>
                <p className="mt-1 text-xs text-espresso-light">
                  <span data-testid="product-cookie-remaining">{launchPromo.remaining}</span> of {launchPromo.limit} left.
                </p>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* WHAT PEOPLE THINK — live Google reviews, no internal review threshold */}
      <section
        id="reviews"
        className="mt-24 border-t border-line pt-14 md:mt-32"
        data-testid="google-reviews-section"
      >
        <div className="label-xs text-espresso-light">What people think</div>
        <div className="mt-6 flex flex-wrap items-end justify-between gap-6">
          <div>
            <h2 className="font-serif text-4xl leading-tight text-espresso md:text-5xl">
              Loved by people<br/>
              <span className="italic text-espresso-light">on Google.</span>
            </h2>
            {googleReviews?.rating != null && (
              <div
                className="mt-5 flex flex-wrap items-center gap-3"
                data-testid="product-google-rating"
              >
                <Stars value={googleReviews.rating} size={20} showValue />
                <span className="text-sm text-espresso-light">
                  Based on {googleReviews.total || 0} Google review
                  {googleReviews.total === 1 ? "" : "s"}
                </span>
              </div>
            )}
          </div>
          <span
            className={
              "inline-flex items-center gap-2 border border-line px-3 py-2 "
              + "label-xs text-espresso"
            }
            data-testid="verified-on-google-badge"
          >
            <BadgeCheck size={15} className="text-gold-hover" aria-hidden="true" />
            Verified on Google
          </span>
        </div>

        {googleFiveStarReviews.length > 0 && (
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {googleFiveStarReviews.map((review, index) => (
              <article
                key={`${review.time || "google"}-${index}`}
                className="flex h-full flex-col border border-line bg-ivory p-6 md:p-8"
                data-testid="product-google-review-card"
              >
                <div className="flex items-center gap-3">
                  {review.profile_photo_url ? (
                    <img
                      src={review.profile_photo_url}
                      alt={review.author_name || "Google reviewer"}
                      className="h-10 w-10 rounded-full object-cover"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <div
                      className={
                        "flex h-10 w-10 items-center justify-center rounded-full bg-ivory-100 "
                        + "font-serif text-espresso"
                      }
                    >
                      {(review.author_name || "?").slice(0, 1)}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="truncate font-serif text-lg text-espresso">
                      {review.author_name || "Google reviewer"}
                    </div>
                    <div className="label-xs text-espresso-light">
                      {review.relative_time_description}
                    </div>
                  </div>
                </div>
                <Stars value={review.rating || 0} size={14} className="mt-4" />
                {review.text && (
                  <p
                    className="mt-4 font-serif text-lg italic leading-snug text-espresso md:text-xl"
                    data-testid="product-google-review-text"
                  >
                    “{review.text}”
                  </p>
                )}
              </article>
            ))}
          </div>
        )}

        {!googleReviewsLoading && googleFiveStarReviews.length > 0 && (
          <div className="mt-10 text-center">
            <a
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noreferrer"
              className="label-xs link-underline text-espresso"
              data-testid="product-read-google-reviews"
            >
              Read all on Google →
            </a>
          </div>
        )}
      </section>
    </main>
  );
}
